import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package, User, Mail } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { useCustomer } from "@/context/CustomerAuthContext";
import { BRAND } from "@/lib/brand";
import StarLoader from "@/components/StarLoader";

export default function Account() {
  const { customer, ready, logout, signInWithGoogle } = useCustomer();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) { setLoading(false); return; }
    api.get("/customer/orders")
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [customer]);

  if (!ready) {
    return <StarLoader full label="Loading your account" />;
  }

  if (!customer) {
    return (
      <main className="mx-auto max-w-md px-5 py-24 text-center" data-testid="account-signin-page">
        <User size={40} className="mx-auto text-sj-muted" />
        <h1 className="font-display text-3xl mt-6 font-light">Sign in to your account</h1>
        <p className="text-sj-muted mt-3">See your order history, save your details, and check out faster.</p>
        <button onClick={signInWithGoogle} className="btn-primary mt-10" data-testid="account-signin-google">
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 5.9 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.3-11.3-8L6.2 32.9C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.2-.1-2.3-.5-3.5z"/></svg>
          Continue with Google
        </button>
      </main>
    );
  }

  const doLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <main className="mx-auto max-w-[1200px] px-5 sm:px-10 py-14 sm:py-20" data-testid="account-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {customer.picture ? (
            <img src={customer.picture} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-sj-gold/40" data-testid="account-avatar" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-sj-gold/20 flex items-center justify-center text-sj-goldDark font-display text-xl">
              {customer.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <div className="overline">My account</div>
            <h1 className="font-display text-3xl font-light mt-1" data-testid="account-name">{customer.name}</h1>
            <div className="text-sm text-sj-muted" data-testid="account-email">{customer.email}</div>
          </div>
        </div>
        <button onClick={doLogout} className="btn-outline text-sm self-start" data-testid="account-logout">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="mt-12">
        <div className="flex items-center gap-2 mb-6">
          <Package size={18} className="text-sj-goldDark" />
          <h2 className="font-display text-xl">Your orders</h2>
          <span className="text-sm text-sj-muted">({orders.length})</span>
        </div>

        {loading ? (
          <div className="text-sj-muted">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="bg-sj-cream rounded-md p-14 text-center" data-testid="account-no-orders">
            <div className="text-sj-inkSoft">No orders yet. Let’s change that ✨</div>
            <Link to="/shop" className="btn-primary mt-6">Shop the collection</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="account-orders-list">
            {orders.map((o) => (
              <details key={o.id} className="bg-white border border-sj-ink/10 rounded-md" data-testid={`account-order-${o.id}`}>
                <summary className="p-5 flex items-center justify-between cursor-pointer list-none">
                  <div>
                    <div className="text-xs text-sj-muted">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="font-display text-base mt-1">Order {o.id.slice(0, 8)}</div>
                    <div className="text-xs text-sj-muted mt-0.5">{o.items.length} item{o.items.length > 1 ? "s" : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg">₹{Number(o.total).toLocaleString("en-IN")}</div>
                    {/* ONE badge only — reflects the order status set by admin */}
                    <span className={`mt-1 text-[11px] inline-block px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      o.status === "delivered" || o.status === "completed" ? "bg-green-100 text-green-700" :
                      o.status === "shipped"   ? "bg-blue-100 text-blue-700" :
                      "bg-amber-100 text-amber-700"
                    }`} data-testid={`account-order-status-${o.id}`}>
                      {(o.status || "pending").replace(/_/g, " ")}
                    </span>
                  </div>
                </summary>
                <div className="border-t border-sj-ink/10 p-5 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <div className="overline mb-3">Ship to</div>
                    <div className="text-sj-inkSoft">{o.address}, {o.city} — {o.pincode}</div>
                    <div className="mt-2 text-sj-inkSoft">📞 {o.phone}</div>
                  </div>
                  <div>
                    <div className="overline mb-3">Items</div>
                    <ul className="space-y-2">
                      {o.items.map((it, i) => (
                        <li key={i} className="flex gap-3 items-center">
                          {it.image && <div className="w-12 h-12 bg-sj-cream overflow-hidden shrink-0"><img src={resolveImage(it.image, { w: 120 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" /></div>}
                          <div className="flex-1 flex justify-between">
                            <span className="text-sj-inkSoft line-clamp-1">{it.name} × {it.quantity}</span>
                            <span>₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      <div className="mt-16 p-6 bg-sj-cream rounded-md flex items-center justify-between gap-4">
        <div>
          <div className="font-display text-lg">Need help with an order?</div>
          <div className="text-sm text-sj-muted mt-1">We usually reply within a few hours.</div>
        </div>
        <a href={`mailto:${BRAND.email}?subject=Need%20help%20with%20my%20order`} className="btn-gold text-sm">
          <Mail size={14} /> Email us
        </a>
      </div>
    </main>
  );
}
